import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Platform } from 'react-native';
import { api } from '../../api/remembrApi';
import { logger } from '../../utils/logger';
import { analytics } from '../../utils/analytics';
import { clarity, clarityHelpers } from '../../utils/clarity';
import { processApiError } from '../../utils/errorUtils';
import { SecureStorage } from '../../utils/storage';
import type { RootState } from '../../store/store';
import type { 
  User, 
  LoginRequest, 
  RegisterRequest, 
  LoginResponse, 
  RegisterResponse,
  VerifyEmailRequest,
  PasswordResetRequest,
  ResetPasswordRequest,
  UpdateProfileRequest,
  UpdatePasswordRequest,
  UpdateEmailRequest,
  ValidationError,
} from '../../api/types';

interface AuthState {
  // User data
  user: User | null;
  accessToken: string | null;
  verificationToken: string | null;
  
  // Status flags
  isAuthenticated: boolean;
  isVerified: boolean;
  requiresVerification: boolean;
  
  // Loading states
  isLoading: boolean;
  isVerifying: boolean;
  isResettingPassword: boolean;
  isUpdatingProfile: boolean;
  
  // Error states
  error: string | null;
  validationErrors: ValidationError[] | null;
  
  // Timer state for verification
  verificationExpiresAt: number | null;
  
  // Temporary credentials for auto-login after verification
  tempCredentials: { email: string; password: string } | null;
}

const initialState: AuthState = {
  // User data
  user: null,
  accessToken: null,
  verificationToken: null,
  
  // Status flags
  isAuthenticated: false,
  isVerified: false,
  requiresVerification: false,
  
  // Loading states
  isLoading: false,
  isVerifying: false,
  isResettingPassword: false,
  isUpdatingProfile: false,
  
  // Error states
  error: null,
  validationErrors: null,
  
  // Timer state for verification
  verificationExpiresAt: null,
  
  // Temporary credentials for auto-login after verification
  tempCredentials: null,
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      console.log('🏁 Starting login process...');
      const response = await api.login(credentials);
      console.log('🎯 Auth slice - login response:', response);
      
      console.log('💾 Storing access token...');
      await SecureStorage.setItem('auth_token', response.access_token);
      
      logger.info('User logged in successfully', { 
        userId: response.user?.id, 
        tokenType: response.token_type,
        requiresVerification: (response as LoginResponse).requires_verification
      });

      // Track successful login
      analytics.trackLogin('email');
      
      return response as LoginResponse;
    } catch (error: any) {
      logger.error('Login failed', error);
      const processedError = processApiError(error);
      return rejectWithValue(processedError);
    }
  }
);

/**
 * Sign in with a Google ID token (GIS credential).
 *
 * Success + failure shape intentionally mirrors `loginUser` so existing
 * screens and selectors keep working without special-casing the provider.
 * The backend auto-links to an existing password account when the verified
 * Google email matches, so we never need a "link accounts" UI step.
 */
export const loginWithGoogle = createAsyncThunk(
  'auth/loginWithGoogle',
  async ({ idToken }: { idToken: string }, { rejectWithValue }) => {
    try {
      console.log('🏁 Starting Google login…');
      const response = await api.loginWithGoogle(idToken);
      console.log('🎯 Auth slice - Google login response:', response);

      console.log('💾 Storing access token (Google)…');
      await SecureStorage.setItem('auth_token', response.access_token);

      logger.info('User logged in with Google', {
        userId: response.user?.id,
        tokenType: response.token_type,
        requiresVerification: response.requires_verification,
      });

      // Funnel Google logins into the same analytics bucket as email logins
      // but tag the method so we can slice them in Clarity/analytics later.
      analytics.trackLogin('google');

      return response as LoginResponse;
    } catch (error: any) {
      logger.error('Google login failed', error);
      const processedError = processApiError(error);
      return rejectWithValue(processedError);
    }
  }
);

/**
 * Disable GIS auto-select on web. Call this whenever the local session
 * ends — otherwise One Tap silently re-signs the user in on the next
 * `/login` mount and effectively defeats logout.
 *
 * Safe to call any time: it no-ops on native, on SSR, and when GIS
 * hasn't been loaded yet.
 */
const disableGoogleAutoSelect = () => {
  if (Platform.OS !== 'web') return;
  if (typeof window === 'undefined') return;
  try {
    // @ts-ignore — `google` is injected by the GIS script at runtime.
    window.google?.accounts?.id?.disableAutoSelect?.();
  } catch (err) {
    // Non-fatal: logout local state cleanup still proceeds.
    console.warn('Failed to disable Google auto-select:', err);
  }
};

export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData: RegisterRequest, { rejectWithValue }) => {
    try {
      // Note: Register returns RegisterResponse, not AuthResponse
      const response = await api.register(userData) as any; // Will be RegisterResponse after API update
      
      // If backend provides access token during registration, store it
      if (response.access_token) {
        console.log('💾 Storing access token from registration...');
        await SecureStorage.setItem('auth_token', response.access_token);
      }
      
      logger.info('User registered successfully', { userId: response.user.id });
      
      // Track successful registration
      analytics.trackRegistration();
      
      // Return both response and credentials for storage
      return { 
        ...response as RegisterResponse, 
        tempCredentials: { email: userData.email, password: userData.password }
      };
    } catch (error: any) {
      logger.error('Registration failed', error);
      const processedError = processApiError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const loadUserProfile = createAsyncThunk(
  'auth/loadProfile',
  async (_, { rejectWithValue }) => {
    try {
      const token = await SecureStorage.getItem('auth_token');
      
      if (!token) {
        // Don't treat missing token as an error - it's expected on first visit
        logger.info('No auth token found, user needs to login');
        // Throw a special error type that won't be displayed to user
        const silentError = new Error('NO_TOKEN');
        silentError.name = 'NO_TOKEN';
        throw silentError;
      }
      
      logger.info('Loading user profile with token');
      const user = await api.getProfile();
      logger.info('User profile loaded successfully', { userId: user.id });
      return { user, token };
    } catch (error: any) {
      // Only log actual API errors, not missing tokens
      if (error.name !== 'NO_TOKEN') {
        logger.error('Failed to load user profile', {
          error: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
      }
      
      // Clean up tokens only for authentication errors (401/403)
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('🗑️ Cleaning up invalid auth token due to 401/403 error');
        await SecureStorage.removeItem('auth_token');
      }
      
      // For missing tokens, return a special rejection that won't show errors
      if (error.name === 'NO_TOKEN') {
        return rejectWithValue({ silent: true, message: 'No token found' });
      }
      
      const processedError = processApiError(error);
      return rejectWithValue(processedError);
    }
  }
);

// Email verification thunks
export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async ({ code, token }: { code: string; token: string }, { rejectWithValue }) => {
    try {
      const response = await api.verifyEmail(code, token);
      logger.info('Email verified successfully');
      
      // Track successful email verification
      analytics.trackEmailVerification(true);
      
      return response;
    } catch (error: any) {
      logger.error('Email verification failed', error);
      
      // Track failed email verification
      analytics.trackEmailVerification(false);
      
      const processedError = processApiError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const resendVerificationCode = createAsyncThunk(
  'auth/resendVerificationCode',
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await api.resendVerificationCode(token);
      logger.info('Verification code resent successfully');
      return response;
    } catch (error: any) {
      logger.error('Failed to resend verification code', error);
      const processedError = processApiError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const requestVerificationToken = createAsyncThunk(
  'auth/requestVerificationToken',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await api.requestVerificationToken(credentials);
      logger.info('Verification token requested successfully');
      return response;
    } catch (error: any) {
      logger.error('Failed to request verification token', error);
      const processedError = processApiError(error);
      return rejectWithValue(processedError);
    }
  }
);

// Password reset thunks
export const requestPasswordReset = createAsyncThunk(
  'auth/requestPasswordReset',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await api.requestPasswordReset(email);
      logger.info('Password reset requested successfully');
      
      // Track password reset request
      analytics.trackPasswordReset('request');
      
      return response;
    } catch (error: any) {
      logger.error('Failed to request password reset', error);
      const processedError = processApiError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ code, newPassword, token }: { code: string; newPassword: string; token: string }, { rejectWithValue }) => {
    try {
      const response = await api.resetPassword(code, newPassword, token);
      logger.info('Password reset successfully');
      
      // Track password reset completion
      analytics.trackPasswordReset('complete');
      
      return response;
    } catch (error: any) {
      logger.error('Password reset failed', error);
      const processedError = processApiError(error);
      return rejectWithValue(processedError);
    }
  }
);

// Profile management thunks
export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (data: UpdateProfileRequest, { rejectWithValue }) => {
    try {
      const response = await api.updateProfile(data);
      logger.info('Profile updated successfully');
      return response;
    } catch (error: any) {
      logger.error('Failed to update profile', error);
      const processedError = processApiError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const updatePassword = createAsyncThunk(
  'auth/updatePassword',
  async (data: UpdatePasswordRequest, { rejectWithValue }) => {
    try {
      const response = await api.updatePassword(data);
      logger.info('Password updated successfully');
      return response;
    } catch (error: any) {
      logger.error('Failed to update password', error);
      const processedError = processApiError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const updateEmail = createAsyncThunk(
  'auth/updateEmail',
  async (data: UpdateEmailRequest, { rejectWithValue }) => {
    try {
      const response = await api.updateEmail(data);
      logger.info('Email update initiated successfully');
      return response;
    } catch (error: any) {
      logger.error('Failed to update email', error);
      const processedError = processApiError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const getUserStats = createAsyncThunk(
  'auth/getUserStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.getUserStats();
      logger.info('User stats retrieved successfully');
      return response;
    } catch (error: any) {
      logger.error('Failed to get user stats', error);
      const processedError = processApiError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const deleteAccount = createAsyncThunk(
  'auth/deleteAccount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.deleteAccount();
      logger.info('Account deleted successfully');
      return response;
    } catch (error: any) {
      logger.error('Failed to delete account', error);
      const processedError = processApiError(error);
      return rejectWithValue(processedError);
    }
  }
);

// Enhanced logout with optional backend call
export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (options: { callBackend?: boolean } = {}, { rejectWithValue }) => {
    try {
      console.log('🚪 AuthSlice: logoutUser thunk called with options:', options);
      logger.info('🚪 AuthSlice: Starting logout process', { callBackend: options.callBackend });
      
      // Optionally call backend to invalidate token
      if (options.callBackend) {
        try {
          console.log('🚪 AuthSlice: About to call api.logout()');
          logger.info('🚪 AuthSlice: Calling backend logout');
          await api.logout();
          console.log('✅ AuthSlice: api.logout() completed successfully');
          logger.info('✅ AuthSlice: Backend logout successful');
        } catch (backendError: any) {
          console.error('❌ AuthSlice: Backend logout failed:', backendError);
          logger.error('❌ AuthSlice: Backend logout failed, continuing with local logout', {
            error: backendError.message,
            status: backendError.response?.status,
          });
          // Continue with local logout even if backend fails
        }
      }
      
      // Always clear local storage
      console.log('🧹 AuthSlice: Starting local storage cleanup');
      logger.info('🧹 AuthSlice: Clearing local storage');
      await SecureStorage.clearAuthStorage();

      // Kill GIS auto-select so One Tap doesn't silently re-sign-in
      // the user on the next /login mount.
      disableGoogleAutoSelect();
      
      console.log('✅ AuthSlice: Logout process completed successfully');
      logger.info('✅ AuthSlice: Logout completed successfully');
      return { success: true };
    } catch (error: any) {
      // Even if backend logout fails, we still clear local data
      console.error('❌ AuthSlice: Logout error occurred:', error);
      logger.error('❌ AuthSlice: Logout error occurred, but clearing local data', error);
      
      await SecureStorage.clearAuthStorage();
      disableGoogleAutoSelect();

      console.log('✅ AuthSlice: Local logout completed despite errors');
      logger.info('✅ AuthSlice: Local logout completed despite errors');
      // Don't reject - logout should always succeed locally
      return { success: true, backendError: true };
    }
  }
);

// Auto-login after verification for seamless UX
export const autoLoginAfterVerification = createAsyncThunk(
  'auth/autoLoginAfterVerification',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as any;
      const tempCredentials = state.auth.tempCredentials;
      
      if (!tempCredentials) {
        throw new Error('No credentials available for auto-login');
      }
      
      console.log('🔄 Auto-login after verification with stored credentials');
      
      const response = await api.login({
        username: tempCredentials.email,
        password: tempCredentials.password,
      });
      
      // Store the access token
      await SecureStorage.setItem('auth_token', response.access_token);
      
      logger.info('Auto-login after verification successful');
      return response as LoginResponse;
    } catch (error: any) {
      logger.error('Auto-login after verification failed', error);
      const processedError = processApiError(error);
      return rejectWithValue(processedError);
    }
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.verificationToken = null;
      state.isAuthenticated = false;
      state.isVerified = false;
      state.requiresVerification = false;
      state.verificationExpiresAt = null;
      state.tempCredentials = null;
      state.error = null;
      state.validationErrors = null;

      // Clear auth storage
      SecureStorage.clearAuthStorage().catch((error) => {
        console.warn('Failed to clear auth storage during logout:', error);
      });

      // Disable GIS auto-select so One Tap doesn't silently re-auth.
      disableGoogleAutoSelect();

      logger.info('User logged out');
    },
    clearError: (state) => {
      state.error = null;
      state.validationErrors = null;
    },
    setVerificationToken: (state, action: PayloadAction<{ token: string; expiresIn: number }>) => {
      state.verificationToken = action.payload.token;
      state.verificationExpiresAt = Date.now() + (action.payload.expiresIn * 1000);
    },
    clearVerificationToken: (state) => {
      state.verificationToken = null;
      state.verificationExpiresAt = null;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.validationErrors = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.accessToken = action.payload.access_token;
        state.isAuthenticated = true;
        state.isVerified = action.payload.user.is_verified;
        state.requiresVerification = !action.payload.user.is_verified;
        state.tempCredentials = null;
        state.error = null;
        state.validationErrors = null;

        // Track successful login with Clarity
        clarityHelpers.trackAuth('login', 'email');
        clarityHelpers.trackFeature('auth', 'login_success', {
          verified: action.payload.user.is_verified.toString(),
          userId: action.payload.user.id.toString(),
        });
        
        // Set user ID in Clarity for session tracking
        clarity.setUserId(action.payload.user.id.toString());
        
        // If user is not verified, store the verification token
        if (action.payload.verification_token) {
          state.verificationToken = action.payload.verification_token;
          state.verificationExpiresAt = Date.now() + ((action.payload.verification_expires_in || 1800) * 1000);
        }
        
        console.log('🎯 Login fulfilled - state updated:', {
          hasToken: !!state.accessToken,
          hasUser: !!state.user,
          isAuthenticated: state.isAuthenticated,
          isVerified: state.isVerified,
          requiresVerification: state.requiresVerification
        });
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        const errorPayload = action.payload as any;
        state.error = errorPayload.message;
        state.validationErrors = errorPayload.validationErrors;

        // Track login failure with Clarity
        clarityHelpers.trackError(errorPayload.message || 'Login failed', 'auth_login');
        clarityHelpers.trackFeature('auth', 'login_failed', {
          error_type: errorPayload.validationErrors ? 'validation' : 'authentication',
          error_message: errorPayload.message,
        });
      });

    // Google Login — response shape matches /auth/login, so the
    // state mutations are deliberately identical. Diverging them later
    // (e.g. to suppress verification_token path since Google emails are
    // pre-verified) is fine, but keep them in lockstep until a reason
    // to split shows up.
    builder
      .addCase(loginWithGoogle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.validationErrors = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.accessToken = action.payload.access_token;
        state.isAuthenticated = true;
        state.isVerified = action.payload.user.is_verified;
        state.requiresVerification = !action.payload.user.is_verified;
        state.tempCredentials = null;
        state.error = null;
        state.validationErrors = null;

        clarityHelpers.trackAuth('login', 'google');
        clarityHelpers.trackFeature('auth', 'login_success', {
          method: 'google',
          verified: action.payload.user.is_verified.toString(),
          userId: action.payload.user.id.toString(),
        });
        clarity.setUserId(action.payload.user.id.toString());

        if (action.payload.verification_token) {
          state.verificationToken = action.payload.verification_token;
          state.verificationExpiresAt =
            Date.now() + ((action.payload.verification_expires_in || 1800) * 1000);
        }
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.isLoading = false;
        const errorPayload = action.payload as any;
        state.error = errorPayload?.message ?? 'Google sign-in failed';
        state.validationErrors = errorPayload?.validationErrors ?? null;

        clarityHelpers.trackError(errorPayload?.message || 'Google login failed', 'auth_login_google');
        clarityHelpers.trackFeature('auth', 'login_failed', {
          method: 'google',
          error_type: errorPayload?.validationErrors ? 'validation' : 'authentication',
          error_message: errorPayload?.message,
        });
      });

    // Register
    builder
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.validationErrors = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.verificationToken = action.payload.verification_token;
        state.verificationExpiresAt = Date.now() + (action.payload.expires_in * 1000);
        
        // Store temporary credentials for auto-login after verification
        state.tempCredentials = action.payload.tempCredentials;

        // Track successful registration with Clarity
        clarityHelpers.trackAuth('register', 'email');
        clarityHelpers.trackFeature('auth', 'registration_success', {
          userId: action.payload.user.id.toString(),
        });
        
        // Set user ID in Clarity for session tracking
        clarity.setUserId(action.payload.user.id.toString());
        
        // If backend provides access token during registration, set as authenticated
        if (action.payload.access_token) {
          state.accessToken = action.payload.access_token;
          state.isAuthenticated = true;
        } else {
          state.isAuthenticated = false; // User needs to login after verification
        }
        
        state.isVerified = false;
        state.requiresVerification = true;
        state.error = null;
        state.validationErrors = null;
        
        console.log('🎯 Registration fulfilled - state updated:', {
          hasUser: !!state.user,
          hasAccessToken: !!state.accessToken,
          hasVerificationToken: !!state.verificationToken,
          isAuthenticated: state.isAuthenticated,
          isVerified: state.isVerified,
          requiresVerification: state.requiresVerification,
        });
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        const errorPayload = action.payload as any;
        state.error = errorPayload.message;
        state.validationErrors = errorPayload.validationErrors;
        
        // Track registration failure with Clarity
        clarityHelpers.trackError(errorPayload.message || 'Registration failed', 'auth_register');
        clarityHelpers.trackFeature('auth', 'registration_failed', {
          error_type: errorPayload.validationErrors ? 'validation' : 'server',
          error_message: errorPayload.message,
        });
      });

    // Load Profile
    builder
      .addCase(loadUserProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loadUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.accessToken = action.payload.token;
        state.isAuthenticated = true;
        state.isVerified = action.payload.user.is_verified;
        state.requiresVerification = !action.payload.user.is_verified;
        state.tempCredentials = null;
        state.error = null;
        state.validationErrors = null;
      })
      .addCase(loadUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.user = null;
        state.accessToken = null;
        state.verificationToken = null;
        state.isAuthenticated = false;
        state.isVerified = false;
        state.requiresVerification = false;
        state.verificationExpiresAt = null;
        state.tempCredentials = null;
        
        const errorPayload = action.payload as any;
        
        // Only show errors for non-silent failures (actual API errors, not missing tokens)
        if (!errorPayload?.silent) {
          state.error = errorPayload.message;
          state.validationErrors = errorPayload.validationErrors;
        } else {
          // For silent errors (missing tokens), don't show any error to user
          state.error = null;
          state.validationErrors = null;
        }
      });

    // Email Verification
    builder
      .addCase(verifyEmail.pending, (state) => {
        state.isVerifying = true;
        state.error = null;
        state.validationErrors = null;
      })
      .addCase(verifyEmail.fulfilled, (state) => {
        state.isVerifying = false;
        state.isVerified = true;
        state.requiresVerification = false;
        
        // Track successful email verification with Clarity
        clarityHelpers.trackAuth('verify_email', 'otp');
        clarityHelpers.trackFeature('auth', 'email_verification_success', {
          userId: state.user?.id?.toString() || 'unknown',
          wasAuthenticated: state.accessToken ? 'true' : 'false',
        });
        
        // If user was already authenticated (has access token), keep them authenticated
        // Otherwise, they'll need to login after verification
        if (state.accessToken) {
          state.isAuthenticated = true;
        }
        
        if (state.user) {
          state.user.is_verified = true;
        }
        state.verificationToken = null;
        state.verificationExpiresAt = null;
        state.error = null;
        state.validationErrors = null;
        
        console.log('🎯 Email verification fulfilled - state updated:', {
          hasAccessToken: !!state.accessToken,
          isAuthenticated: state.isAuthenticated,
          isVerified: state.isVerified,
          requiresVerification: state.requiresVerification,
          userVerified: state.user?.is_verified,
        });
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.isVerifying = false;
        const errorPayload = action.payload as any;
        state.error = errorPayload.message;
        state.validationErrors = errorPayload.validationErrors;
        
        // Track email verification failure with Clarity
        clarityHelpers.trackError(errorPayload.message || 'Email verification failed', 'auth_verify_email');
        clarityHelpers.trackFeature('auth', 'email_verification_failed', {
          error_type: errorPayload.validationErrors ? 'validation' : 'server',
          error_message: errorPayload.message,
        });
      });

    // Resend Verification Code
    builder
      .addCase(resendVerificationCode.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.validationErrors = null;
      })
      .addCase(resendVerificationCode.fulfilled, (state) => {
        state.isLoading = false;
        // Reset timer - new code sent
        state.verificationExpiresAt = Date.now() + (30 * 60 * 1000); // 30 minutes
        state.error = null;
        state.validationErrors = null;
        
        // Track resend verification code with Clarity
        clarityHelpers.trackFeature('auth', 'resend_verification_code', {
          userId: state.user?.id?.toString() || 'unknown',
        });
      })
      .addCase(resendVerificationCode.rejected, (state, action) => {
        state.isLoading = false;
        const errorPayload = action.payload as any;
        state.error = errorPayload.message;
        state.validationErrors = errorPayload.validationErrors;
      });

    // Request Verification Token
    builder
      .addCase(requestVerificationToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.validationErrors = null;
      })
      .addCase(requestVerificationToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.verificationToken = action.payload.verification_token;
        state.verificationExpiresAt = Date.now() + (action.payload.expires_in * 1000);
        state.user = action.payload.user;
        state.requiresVerification = true;
        state.isVerified = false;
        state.error = null;
        state.validationErrors = null;
        
        // Store credentials for auto-login after verification (for login flow)
        state.tempCredentials = {
          email: action.meta.arg.username,
          password: action.meta.arg.password,
        };
        
        console.log('🎯 Verification token request fulfilled - state updated:', {
          hasVerificationToken: !!state.verificationToken,
          requiresVerification: state.requiresVerification,
          userEmail: state.user?.email,
          hasTempCredentials: !!state.tempCredentials,
        });
      })
      .addCase(requestVerificationToken.rejected, (state, action) => {
        state.isLoading = false;
        const errorPayload = action.payload as any;
        state.error = errorPayload.message;
        state.validationErrors = errorPayload.validationErrors;
      });

    // Password Reset Request
    builder
      .addCase(requestPasswordReset.pending, (state) => {
        state.isResettingPassword = true;
        state.error = null;
        state.validationErrors = null;
      })
      .addCase(requestPasswordReset.fulfilled, (state, action) => {
        state.isResettingPassword = false;
        state.verificationToken = action.payload.reset_token;
        state.verificationExpiresAt = Date.now() + (action.payload.expires_in * 1000);
        state.error = null;
        state.validationErrors = null;
        
        // Track password reset request with Clarity
        clarityHelpers.trackFeature('auth', 'password_reset_request', {
          email: action.meta.arg,
        });
      })
      .addCase(requestPasswordReset.rejected, (state, action) => {
        state.isResettingPassword = false;
        const errorPayload = action.payload as any;
        state.error = errorPayload.message;
        state.validationErrors = errorPayload.validationErrors;
      });

    // Reset Password
    builder
      .addCase(resetPassword.pending, (state) => {
        state.isResettingPassword = true;
        state.error = null;
        state.validationErrors = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isResettingPassword = false;
        state.verificationToken = null;
        state.verificationExpiresAt = null;
        state.error = null;
        state.validationErrors = null;
        
        // Track successful password reset with Clarity
        clarityHelpers.trackFeature('auth', 'password_reset_success');
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isResettingPassword = false;
        const errorPayload = action.payload as any;
        state.error = errorPayload.message;
        state.validationErrors = errorPayload.validationErrors;
      });

    // Profile Update
    builder
      .addCase(updateProfile.pending, (state) => {
        state.isUpdatingProfile = true;
        state.error = null;
        state.validationErrors = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isUpdatingProfile = false;
        state.user = action.payload;
        state.error = null;
        state.validationErrors = null;
        
        // Track successful profile update with Clarity
        clarityHelpers.trackFeature('auth', 'profile_update_success', {
          userId: action.payload.id.toString(),
        });
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isUpdatingProfile = false;
        const errorPayload = action.payload as any;
        state.error = errorPayload.message;
        state.validationErrors = errorPayload.validationErrors;
      });

    // Password Update
    builder
      .addCase(updatePassword.pending, (state) => {
        state.isUpdatingProfile = true;
        state.error = null;
        state.validationErrors = null;
      })
      .addCase(updatePassword.fulfilled, (state) => {
        state.isUpdatingProfile = false;
        state.error = null;
        state.validationErrors = null;
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.isUpdatingProfile = false;
        const errorPayload = action.payload as any;
        state.error = errorPayload.message;
        state.validationErrors = errorPayload.validationErrors;
      });

    // Email Update
    builder
      .addCase(updateEmail.pending, (state) => {
        state.isUpdatingProfile = true;
        state.error = null;
        state.validationErrors = null;
      })
      .addCase(updateEmail.fulfilled, (state, action) => {
        state.isUpdatingProfile = false;
        state.verificationToken = action.payload.verification_token;
        state.verificationExpiresAt = Date.now() + (action.payload.expires_in * 1000);
        state.error = null;
        state.validationErrors = null;
      })
      .addCase(updateEmail.rejected, (state, action) => {
        state.isUpdatingProfile = false;
        const errorPayload = action.payload as any;
        state.error = errorPayload.message;
        state.validationErrors = errorPayload.validationErrors;
      });

    // Enhanced Logout
    builder
      .addCase(logoutUser.pending, (state) => {
        // Keep user logged in until logout completes
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        // Track logout before clearing state
        clarityHelpers.trackAuth('logout');
        clarityHelpers.trackFeature('auth', 'logout_success');

        // Clear all auth state
        state.user = null;
        state.accessToken = null;
        state.verificationToken = null;
        state.isAuthenticated = false;
        state.isVerified = false;
        state.requiresVerification = false;
        state.verificationExpiresAt = null;
        state.tempCredentials = null;
        state.error = null;
        state.validationErrors = null;
        state.isLoading = false;
        state.isVerifying = false;
        state.isResettingPassword = false;
        state.isUpdatingProfile = false;
      })
      .addCase(logoutUser.rejected, (state) => {
        // Even if logout "fails", we still clear everything locally
        state.user = null;
        state.accessToken = null;
        state.verificationToken = null;
        state.isAuthenticated = false;
        state.isVerified = false;
        state.requiresVerification = false;
        state.verificationExpiresAt = null;
        state.tempCredentials = null;
        state.error = null;
        state.validationErrors = null;
        state.isLoading = false;
        state.isVerifying = false;
        state.isResettingPassword = false;
        state.isUpdatingProfile = false;
      });

    // Auto-login after verification
    builder
      .addCase(autoLoginAfterVerification.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(autoLoginAfterVerification.fulfilled, (state, action) => {
        state.isLoading = false;
        state.accessToken = action.payload.access_token;
        state.isAuthenticated = true;
        state.isVerified = true;
        state.requiresVerification = false;
        // Clear temporary credentials after successful auto-login
        state.tempCredentials = null;
        state.error = null;
        state.validationErrors = null;
      })
      .addCase(autoLoginAfterVerification.rejected, (state, action) => {
        state.isLoading = false;
        // If auto-login fails, clear temp credentials and show error
        state.tempCredentials = null;
        const errorPayload = action.payload as any;
        state.error = errorPayload.message;
        state.validationErrors = errorPayload.validationErrors;
      });
  },
});

export const { logout, clearError, setVerificationToken, clearVerificationToken } = authSlice.actions;

// Selectors
export const selectAuthState = (state: RootState) => state.auth;
export const selectUser = (state: RootState) => state.auth.user;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectIsVerified = (state: RootState) => state.auth.isVerified;
export const selectRequiresVerification = (state: RootState) => state.auth.requiresVerification;
export const selectVerificationToken = (state: RootState) => state.auth.verificationToken;
export const selectVerificationExpiresAt = (state: RootState) => state.auth.verificationExpiresAt;
export const selectAuthLoading = (state: RootState) => state.auth.isLoading;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectValidationErrors = (state: RootState) => state.auth.validationErrors;
export const selectIsVerifying = (state: RootState) => state.auth.isVerifying;
export const selectIsResettingPassword = (state: RootState) => state.auth.isResettingPassword;
export const selectIsUpdatingProfile = (state: RootState) => state.auth.isUpdatingProfile;

export default authSlice.reducer; 